/**
 * Image Compression Utility
 * 
 * Compresses images before upload to save storage space.
 * Uses canvas-based compression for browser compatibility.
 */

export interface CompressionOptions {
  maxWidth?: number;
  maxHeight?: number;
  quality?: number; // 0-1, default 0.8
  maxSizeKB?: number; // Target max size in KB
}

const DEFAULT_OPTIONS: Required<CompressionOptions> = {
  maxWidth: 1920,
  maxHeight: 1920,
  quality: 0.8,
  maxSizeKB: 500, // 500KB target
};

/**
 * Check if a file is an image that can be compressed
 */
export function isCompressibleImage(file: File): boolean {
  const compressibleTypes = ["image/jpeg", "image/png", "image/webp"];
  return compressibleTypes.includes(file.type);
}

/**
 * Load an image file into an HTMLImageElement
 */
function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new window.Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Failed to load image"));
    img.src = URL.createObjectURL(file);
  });
}

/**
 * Calculate new dimensions maintaining aspect ratio
 */
function calculateDimensions(
  width: number,
  height: number,
  maxWidth: number,
  maxHeight: number
): { width: number; height: number } {
  if (width <= maxWidth && height <= maxHeight) {
    return { width, height };
  }

  const aspectRatio = width / height;

  if (width > height) {
    const newWidth = Math.min(width, maxWidth);
    return {
      width: newWidth,
      height: Math.round(newWidth / aspectRatio),
    };
  } else {
    const newHeight = Math.min(height, maxHeight);
    return {
      width: Math.round(newHeight * aspectRatio),
      height: newHeight,
    };
  }
}

/**
 * Convert canvas to Blob with specified quality
 */
function canvasToBlob(
  canvas: HTMLCanvasElement,
  type: string,
  quality: number
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error("Failed to convert canvas to blob"));
        }
      },
      type,
      quality
    );
  });
}

/**
 * Compress an image file
 * Returns the compressed file, or the original if compression isn't beneficial
 */
export async function compressImage(
  file: File,
  options: CompressionOptions = {}
): Promise<File> {
  // Skip non-compressible images
  if (!isCompressibleImage(file)) {
    console.log(`[ImageCompression] Skipping non-compressible file: ${file.type}`);
    return file;
  }

  // Skip small files (under 100KB)
  if (file.size < 100 * 1024) {
    console.log(`[ImageCompression] Skipping small file: ${(file.size / 1024).toFixed(1)}KB`);
    return file;
  }

  const opts = { ...DEFAULT_OPTIONS, ...options };

  try {
    // Load the image
    const img = await loadImage(file);
    
    // Calculate new dimensions
    const { width, height } = calculateDimensions(
      img.width,
      img.height,
      opts.maxWidth,
      opts.maxHeight
    );

    // Create canvas and draw resized image
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext("2d");
    if (!ctx) {
      throw new Error("Failed to get canvas context");
    }

    // Use high-quality image rendering
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(img, 0, 0, width, height);

    // Clean up object URL
    URL.revokeObjectURL(img.src);

    // Determine output type (prefer WebP for better compression, fallback to original)
    let outputType = file.type;
    
    // Try progressive quality reduction if file is too large
    let quality = opts.quality;
    let blob = await canvasToBlob(canvas, outputType, quality);

    // If still too large and above minimum quality, reduce quality
    const targetSize = opts.maxSizeKB * 1024;
    while (blob.size > targetSize && quality > 0.5) {
      quality -= 0.1;
      blob = await canvasToBlob(canvas, outputType, quality);
    }

    // Only use compressed version if it's actually smaller
    if (blob.size >= file.size) {
      console.log(
        `[ImageCompression] Compression not beneficial: ${(file.size / 1024).toFixed(1)}KB -> ${(blob.size / 1024).toFixed(1)}KB`
      );
      return file;
    }

    // Create new File from blob
    const compressedFile = new File([blob], file.name, {
      type: outputType,
      lastModified: Date.now(),
    });

    const originalSizeKB = (file.size / 1024).toFixed(1);
    const compressedSizeKB = (compressedFile.size / 1024).toFixed(1);
    const savings = (((file.size - compressedFile.size) / file.size) * 100).toFixed(0);

    console.log(
      `[ImageCompression] Compressed: ${originalSizeKB}KB -> ${compressedSizeKB}KB (${savings}% saved)`
    );

    return compressedFile;
  } catch (error) {
    console.error("[ImageCompression] Error compressing image:", error);
    // Return original file on error
    return file;
  }
}

/**
 * Compress multiple image files
 */
export async function compressImages(
  files: File[],
  options: CompressionOptions = {}
): Promise<File[]> {
  return Promise.all(files.map((file) => compressImage(file, options)));
}
